import Api from "../api";
import {
  GET_TEMPLATE,
  GET_TEMPLATE_URL,
  DELETE_TEMPLATE,
  POST_TEMPLATE,
  EDIT_TEMPLATE,
  ADD_MY_SENDER_ID,
  ADD_MY_SENDER_ID_URL,
  UPDATE_MY_SENDER_ID,
  DELETE_MY_SENDER_ID,
  SET_MY_DEFAULT_SENDER_ID,
  SENDER_ID_BY_ID_URL,
  SENDER_ID_DEFAULT_URL,
} from "../lib/constant";
import { handleRejectCatch } from "../lib/utils";

// Transform backend SmsTemplate proto to frontend template shape
function transformTemplate(t) {
  return {
    id: t.id,
    templateId: t.dltTemplateId || t.dlt_template_id || '',
    name: t.templateName || t.template_name || '',
    templateUserName: t.templateName || t.template_name || '',
    templateContent: t.templateText || t.template_text || '',
    content: t.templateText || t.template_text || '',
    text: t.templateText || t.template_text || '',
    senderId: t.senderId || t.sender_id || '',
    header: t.senderId || t.sender_id || '',
    dltTemplateId: t.dltTemplateId || t.dlt_template_id || '',
    dltTempId: t.dltTemplateId || t.dlt_template_id || '',
    // Bank/financial-institute name (transactional) comes back in the metadata map.
    financialInstituteName: (t.metadata && (t.metadata.financial_institute_name || t.metadata.financialInstituteName)) || '',
    // dltCategory removed 2026-08-01 — the DLT class follows the sender's
    // header type and is no longer stored or returned by the backend.
    consentTemplateId: t.consentTemplateId || t.consent_template_id || '',
    // Optional per-template downstream TMD override — backend field is being
    // added in parallel, so tolerate the API not echoing it yet.
    dltTmdIdOverride: t.dltTmdIdOverride || t.dlt_tmd_id_override || '',
    status: t.status || '',
    variables: t.variables || [],
    createdAt: t.createdAt || t.created_at || '',
    updatedAt: t.updatedAt || t.updated_at || '',
  };
}

export function getTemplate() {
  const request = Api.get(GET_TEMPLATE_URL);
  return (dispatch) => {
    request
      .then(({ data }) => {
        const rawList = data?.data?.list || data?.templates || data?.data?.template || [];
        const templates = rawList.map(transformTemplate);
        dispatch({
          type: GET_TEMPLATE,
          payload: { success: true, data: { template: templates }, totalCount: templates.length },
        });
      })
      .catch((err) => {
        console.error('getTemplate error:', err);
        dispatch({
          type: GET_TEMPLATE,
          payload: { success: true, data: { template: [] }, totalCount: 0 },
        });
      });
  };
}

const handleRejectData = (dispatch, data, type) => {
  if (data?.response?.status === 500 && data.response.data?.success === false) {
    dispatch({ type, payload: data.response.data });
  } else {
    handleRejectCatch(dispatch, data, type);
  }
};

export function deleteTemplate(id) {
  const request = Api.delete(`${GET_TEMPLATE_URL}/${id}`);
  return (dispatch) => {
    request
      .then(({ data }) => {
        dispatch({ type: DELETE_TEMPLATE, payload: data });
      })
      .catch((data) => {
        handleRejectData(dispatch, data, DELETE_TEMPLATE);
      });
  };
}

export function postTemplate(params) {
  // Map frontend params to proto CreateSmsTemplateRequest fields
  const apiParams = {
    template_name: params.name || params.templateUserName,
    template_text: params.content || params.templateContent,
    sender_id_ref: params.senderId || params.header,
    dlt_template_id: params.dltTemplateId || params.dltTempId,
    // No dlt_category / message_class here — the backend derives the DLT
    // category from the sender ID's header type.
    // Downstream TMD override — only when this template's chain is approved
    // via a different downstream telemarketer than its header.
    dlt_tmd_id_override: params.dltTmdIdOverride || undefined,
    variables: params.variables || [],
    language: params.language,
    country_code: params.countryCode,
    character_count: params.characterCount,
    segment_count: params.segmentCount,
    is_unicode: params.isUnicode,
  };

  const request = Api.post(GET_TEMPLATE_URL, apiParams);
  return (dispatch) => {
    request
      .then(({ data }) => {
        dispatch({ type: POST_TEMPLATE, payload: data });
      })
      .catch((data) => {
        handleRejectData(dispatch, data, POST_TEMPLATE);
      });
  };
}

export function editTemplate(id, params) {
  // Map frontend params to proto UpdateSmsTemplateRequest fields
  const apiParams = {
    template_name: params.name || params.templateUserName,
    template_text: params.content || params.templateContent,
    sender_id_ref: params.senderId || params.header,
    dlt_template_id: params.dltTemplateId || params.dltTempId,
    // No dlt_category / message_class here — the backend derives the DLT
    // category from the sender ID's header type.
    // Downstream TMD override — only when this template's chain is approved
    // via a different downstream telemarketer than its header.
    dlt_tmd_id_override: params.dltTmdIdOverride || undefined,
    variables: params.variables || [],
    language: params.language,
    country_code: params.countryCode,
    character_count: params.characterCount,
    segment_count: params.segmentCount,
    is_unicode: params.isUnicode,
  };

  const request = Api.put(`${GET_TEMPLATE_URL}/${id}`, apiParams);
  return (dispatch) => {
    request
      .then(({ data }) => {
        dispatch({ type: EDIT_TEMPLATE, payload: data });
      })
      .catch((data) => {
        handleRejectData(dispatch, data, EDIT_TEMPLATE);
      });
  };
}

// Self-service add headers/sender IDs. Backend: POST /api/v1/me/sms_config/sender_ids
// org_admin / tenant_owner role required by gateway.
export function addMySenderId(params) {
  const senderIds = params.senderIds || params.sender_ids || [];
  // No TMD here: AddMySenderIdsRequest carries only sender_ids. New headers
  // start in TMD mode 'direct'; admins set a TMD/mode via the support
  // endpoint afterwards (header edit pencil).
  const apiParams = {
    senderIds,
  };
  const request = Api.post(ADD_MY_SENDER_ID_URL, apiParams);
  return (dispatch) => {
    return request
      .then(({ data }) => {
        dispatch({ type: ADD_MY_SENDER_ID, payload: data });
        return data;
      })
      .catch((data) => {
        handleRejectData(dispatch, data, ADD_MY_SENDER_ID);
        throw data;
      });
  };
}

// Update header — caller passes the existing record + the changed fields.
// We forward the merged shape so the backend only sees fields the user
// actually edited (omitempty on the proto side will preserve untouched fields).
export function updateMySenderId(id, params) {
  // No TMD here: UpdateMySenderIdRequest is customer-scoped and has no TMD
  // fields — ManageHeaders saves TMD/mode via the support dlt_tmd endpoint.
  const apiParams = {
    senderName: params.senderName,
    description: params.description,
    headerType: params.headerType,
    countryCode: params.countryCode,
    maxTps: params.maxTps,
    status: params.status,
  };
  const url = SENDER_ID_BY_ID_URL.replace("{id}", id);
  const request = Api.put(url, apiParams);
  return (dispatch) => {
    return request
      .then(({ data }) => {
        dispatch({ type: UPDATE_MY_SENDER_ID, payload: data });
        return data;
      })
      .catch((data) => {
        handleRejectData(dispatch, data, UPDATE_MY_SENDER_ID);
        throw data;
      });
  };
}

export function deleteMySenderId(id) {
  const url = SENDER_ID_BY_ID_URL.replace("{id}", id);
  const request = Api.delete(url);
  return (dispatch) => {
    return request
      .then(({ data }) => {
        dispatch({ type: DELETE_MY_SENDER_ID, payload: data });
        return data;
      })
      .catch((data) => {
        handleRejectData(dispatch, data, DELETE_MY_SENDER_ID);
        throw data;
      });
  };
}

export function setMyDefaultSenderId(id) {
  const url = SENDER_ID_DEFAULT_URL.replace("{id}", id);
  const request = Api.put(url, {});
  return (dispatch) => {
    return request
      .then(({ data }) => {
        dispatch({ type: SET_MY_DEFAULT_SENDER_ID, payload: data });
        return data;
      })
      .catch((data) => {
        handleRejectData(dispatch, data, SET_MY_DEFAULT_SENDER_ID);
        throw data;
      });
  };
}
